// utils/realtimeManager/subscription/subscribe.ts — channel creation + subscription wiring
import { supabase } from '../../../supabase';
import type {RealtimeChannel, RealtimePostgresChangesPayload} from '@supabase/supabase-js';
import type {SubscriptionConfig} from '../types.js';
import type {RetryScheduler} from './retry.js';
import { reportRealtimeOk, reportRealtimeError, reportRealtimeEvent } from '../../realtimeHealth';

export interface SubscriptionState {
  channel: RealtimeChannel | null;
  isUnsubscribed: boolean;
  generation: number;
}

export function createSubscriber(
  config: SubscriptionConfig,
  state: SubscriptionState,
  retry: RetryScheduler
) {
  return () => {
    if (state.isUnsubscribed) return;

    retry.removeVisibilityHandler();

    // Bump generation BEFORE removing old channel so the old CLOSED
    // callback sees a stale generation and skips scheduleRetry.
    const myGeneration = ++state.generation;

    // Remove old channel without letting its callbacks cause more retries.
    if (state.channel) {
      const oldChannel = state.channel;
      state.channel = null;
      try {
        supabase.removeChannel(oldChannel);
      } catch {
        // Ignore removal errors
      }
    }

    if (state.isUnsubscribed) return;

    try {
      const filterConfig: any = {
        event: config.event,
        schema: config.schema || 'public',
        table: config.table,
      };

      if (config.filter) {
        filterConfig.filter = config.filter;
      }

      // Guard data callback with generation check — prevents stale callbacks
      // from old channels during reconnection race conditions
      const guardedCallback = (payload: RealtimePostgresChangesPayload<any>) => {
        if (state.isUnsubscribed || myGeneration !== state.generation) return;
        reportRealtimeEvent();
        config.callback(payload);
      };

      state.channel = supabase
        .channel(config.channelName)
        .on('postgres_changes', filterConfig, guardedCallback)
        .subscribe((status, err) => {
          // Ignore callbacks from superseded channels
          if (state.isUnsubscribed || myGeneration !== state.generation) return;

          if (status === 'SUBSCRIBED') {
            retry.reset();
            reportRealtimeOk();
            if (import.meta.env.DEV) {
              console.log(`✅ Realtime subscribed: ${config.channelName}`);
            }
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.warn(`⚠️ Realtime ${status}: ${config.channelName}`, err?.message || '');
            reportRealtimeError(`${config.channelName}: ${status}${err?.message ? ` — ${err.message}` : ''}`);
            retry.scheduleRetry();
          } else if (status === 'CLOSED') {
            console.warn(`⚠️ Realtime closed: ${config.channelName}, reconnecting...`);
            reportRealtimeError(`${config.channelName}: CLOSED`);
            retry.scheduleRetry();
          }
        });
    } catch (err) {
      console.error(`❌ Realtime subscribe error: ${config.channelName}`, err);
      retry.scheduleRetry();
    }
  };
}
